/**
 * Extract files from a drop, walking into directories when the browser exposes them.
 *
 * `DataTransfer.files` flattens to nothing useful when a folder is dropped, so the entry API is
 * the only way to make "drag a folder in" work. It's non-standard but universally supported;
 * the plain `.files` path below is the fallback.
 */

const MAX_DEPTH = 8;

function readEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  return new Promise((resolve) => {
    reader.readEntries(
      (entries) => resolve(entries),
      () => resolve([])
    );
  });
}

function fileFromEntry(entry: FileSystemFileEntry): Promise<File | null> {
  return new Promise((resolve) => {
    entry.file(
      (file) => resolve(file),
      () => resolve(null)
    );
  });
}

async function walk(entry: FileSystemEntry, depth: number, out: File[]): Promise<void> {
  if (depth > MAX_DEPTH) return;

  if (entry.isFile) {
    const file = await fileFromEntry(entry as FileSystemFileEntry);
    if (file) out.push(file);
    return;
  }

  if (entry.isDirectory) {
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    // readEntries returns at most ~100 per call; keep going until it returns nothing.
    for (;;) {
      const batch = await readEntries(reader);
      if (batch.length === 0) break;
      for (const child of batch) await walk(child, depth + 1, out);
    }
  }
}

export async function filesFromDataTransfer(dataTransfer: DataTransfer): Promise<File[]> {
  const items = Array.from(dataTransfer.items ?? []);
  const entries = items
    .map((item) => (typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null))
    .filter((entry): entry is FileSystemEntry => entry !== null);

  if (entries.length === 0) return Array.from(dataTransfer.files ?? []);

  const files: File[] = [];
  for (const entry of entries) await walk(entry, 0, files);
  return files;
}
